<template>
  <div>
    <div>
      <div class="management-potential-report-page" id="print_element">
        <div v-if="isPreloader" class="management-potential-report-page__preloader">
          <Preloader />
        </div>

        <template v-else>
          <div>
            <ReportDetailsHeader title="ЭИ-профиль группы" :reportType="'comparison'" />
          </div>

          <ReferenceSection
              title="Управленческий потенциал"
              :opened="openedSections.managementPotential"
              @toggle="toggleSection('managementPotential')"
              :collapsible="false"
          >
            <ManagementPotentialInstruction class="instruction-wrapper" />

            <div class="management-potential-report-page__section">
              <div class="management-potential-report-page__half-block">
                <a-card
                    :bordered="true"
                    class="management-potential-report-page__chart-card pdf-avoid-break"
                >
                  <ManagementPotentialLevelsChart
                      title="Количество сотрудников на каждом уровне Управленческого потенциала"
                      :items="managementPotentialLevels"
                  />
                </a-card>
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Зеленые и красные флаги
                </h3>

                <div class="management-potential-report-page__half-block">
                  <ManagementFlagsBlock
                      :groups="managementFlags.groups"
                      :segments="managementFlags.segments"
                  />
                </div>
              </div>
            </div>
          </ReferenceSection>

          <ReferenceSection
              title="Эмоциональный интеллект"
              :opened="openedSections.emotionalIntelligence"
              @toggle="toggleSection('emotionalIntelligence')"
              :collapsible="false"
          >
            <div class="management-potential-report-page__section">
              <EiGeneralScale
                  class="pdf-avoid-break"
                  :show-title="false"
                  card-title="Средний общий ЭИ"
                  description="Уровень совокупности когнитивных способностей: саморегуляция, влияние на эмоции других, эмпатия, анализ изменений эмоциональных реакций, сознательное применение энергии эмоций для усиления деятельности и мышления."
                  :value="eiGeneralScale.value"
                  :compare-value="eiGeneralScale.compareValue"
                  :show-legend="false"
                  :use-gradient="true"
              />

              <div class="management-potential-report-page__ei-chart-grid">
                <a-card
                    :bordered="true"
                    class="management-potential-report-page__chart-card management-potential-report-page__chart-card--small pdf-avoid-break"
                >
                  <EiLevelEmployeesChart
                      title="Количество сотрудников на каждом уровне Общего ЭИ"
                      description="Сколько респондентов по общему ЭИ попадают в соответствующий уровень."
                      :items="eiLevelBars"
                  />
                </a-card>
              </div>
            </div>
          </ReferenceSection>

          <ReferenceSection
              title="Стратеги и Драйверы"
              :opened="openedSections.strategistsAndDrivers"
              @toggle="toggleSection('strategistsAndDrivers')"
              :collapsible="false"
              :showLine="false"
          >
            <div class="management-potential-report-page__section">
              <div class="management-potential-report-page__notice pdf-avoid-break">
                <InfoCircleFilled class="management-potential-report-page__notice-icon" />

                <span>
                  Чем меньше разрыв между доменами, тем выше личная эффективность.
                  Сильный разрыв ведет к ошибкам в оценке ситуаций.
                </span>
              </div>

              <div class="management-potential-report-page__comparison-grid">
                <RoleDistributionCard
                    :show-tag="false"
                    class="pdf-avoid-break"
                    title="Распределение ролей"
                    description="Количество человек в каждой группе."
                    :items="roleDistribution"
                />

                <DomainDisbalanceCard
                    class="pdf-avoid-break"
                    :show-tag="false"
                    :show-zero-values="true"
                    title="Уровень дисбаланса между доменами"
                    description="Показывает средний разрыв баллов в каждом домене и оценивает гибкость поведения."
                    :rows="domainDisbalance"
                />
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Сильные стороны
                </h3>

                <div class="management-potential-report-page__comparison-grid">
                  <EiScaleRowsCard
                      class="pdf-avoid-break"
                      title="Сильные стороны в ветвях ЭИ"
                      :sections="strongSides"
                  />

                  <BranchDonutCard
                      class="pdf-avoid-break"
                      title="Ведущие ветви ЭИ"
                      description="Количество человек с лидерским показателем в каждой ветви."
                      :items="leadingBranches"
                      :show-additional-text="true"
                      additional-text="Сумма показателей больше количества человек, поскольку один сотрудник может иметь высокие результаты сразу по двум ветвям."
                  />
                </div>
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Зоны роста
                </h3>

                <div class="management-potential-report-page__comparison-grid">
                  <EiScaleRowsCard
                      class="pdf-avoid-break"
                      title="Зоны роста в ветвях ЭИ"
                      :sections="growthZones"
                  />

                  <BranchDonutCard
                      class="pdf-avoid-break"
                      title="Приоритеты развития"
                      description="Количество сотрудников, для которых эта ветвь — самая слабая."
                      :items="developmentPriorities"
                      :show-additional-text="true"
                      additional-text="Сумма показателей больше количества человек, поскольку один сотрудник может иметь низкие результаты сразу по двум ветвям."
                  />
                </div>
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  ЭИ-профиль с сильными сторонами и зонами роста
                </h3>

                <EiStrengthGrowthProfileBlock
                    class="pdf-avoid-break"
                    :show-title="false"
                    :average-profile-sections="eiGrowthProfile"
                    :details-sections="eiDetailsProfile"
                />
              </div>
            </div>
          </ReferenceSection>

          <ReferenceSection
              title="Сравнение по демографическим показателям"
              :opened="openedSections.demographicsComparison"
              @toggle="toggleSection('demographicsComparison')"
              :collapsible="false"
          >
            <div class="management-potential-report-page__section">
              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Группы по полу
                </h3>

                <div class="management-potential-report-page__gender-chart-grid">
                  <a-card
                      :bordered="true"
                      class="management-potential-report-page__chart-card management-potential-report-page__chart-card--gender pdf-avoid-break"
                  >
                    <GenderEiLevelCompareChart
                        title="Сравнение на каждом уровне Общего ЭИ"
                        first-label="Мужчины"
                        second-label="Женщины"
                        first-color="#7D9EE9"
                        second-color="#F29EB6"
                        :items="genderLevelBars"
                    />
                  </a-card>
                </div>

                <GenderEiProfileComparisonBlock
                    class="pdf-avoid-break"
                    first-label="Мужчины"
                    second-label="Женщины"
                    first-color="#7D9EE9"
                    second-color="#F29EB6"
                    :average-profile-sections="genderAverageProfile"
                    :details-sections="genderDetailsProfile"
                />
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Стратеги и Драйверы
                </h3>

                <div class="management-potential-report-page__notice pdf-avoid-break">
                  <InfoCircleFilled class="management-potential-report-page__notice-icon" />

                  <span>
                    Чем меньше разрыв между доменами, тем выше личная эффективность.
                    Сильный разрыв ведет к ошибкам в оценке ситуаций.
                  </span>
                </div>

                <div class="management-potential-report-page__comparison-grid management-potential-report-page__roles">
                  <DemographicRoleDistributionCard
                      class="pdf-avoid-break"
                      title="Распределение ролей"
                      description="Количество человек в каждой группе."
                      :legend-items="genderRoleLegend"
                      :groups="genderRoleDistribution"
                  />

                  <DomainDisbalanceCard
                      class="pdf-avoid-break"
                      :show-tag="false"
                      :show-zero-values="true"
                      title="Уровень дисбаланса между доменами"
                      description="Показывает средний разрыв баллов в каждом домене и оценивает гибкость поведения."
                      group-column-title="Пол"
                      :groups="genderDomainDisbalance"
                  />
                </div>
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Группы по возрасту
                </h3>

                <a-card
                    :bordered="true"
                    class="management-potential-report-page__chart-card management-potential-report-page__chart-card--age pdf-avoid-break"
                >
                  <AgeEiLevelGroupedChart
                      title="Сравнение на каждом уровне Общего ЭИ"
                      :levels="ageLevelGroups"
                  />
                </a-card>

                <AgeEiProfileComparisonBlock
                    class="pdf-avoid-break age-ei-profile-comparison-wrapper"
                    :average-profile-sections="ageAverageProfile"
                    :details-sections="ageDetailsProfile"
                />
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Стратеги и Драйверы
                </h3>

                <div class="management-potential-report-page__notice pdf-avoid-break">
                  <InfoCircleFilled class="management-potential-report-page__notice-icon" />

                  <span>
                    Чем меньше разрыв между доменами, тем выше личная эффективность.
                    Сильный разрыв ведет к ошибкам в оценке ситуаций.
                  </span>
                </div>

                <div class="management-potential-report-page__comparison-grid management-potential-report-page__roles">
                  <DemographicRoleDistributionCard
                      class="pdf-avoid-break"
                      title="Распределение ролей"
                      description="Количество человек в каждой группе."
                      :legend-items="ageRoleLegend"
                      :groups="ageRoleDistribution"
                  />

                  <DomainDisbalanceCard
                      class="pdf-avoid-break"
                      :show-tag="false"
                      :show-zero-values="true"
                      title="Уровень дисбаланса между доменами"
                      description="Показывает средний разрыв баллов в каждом домене и оценивает гибкость поведения."
                      group-column-title="Возраст"
                      :groups="ageDomainDisbalance"
                  />
                </div>
              </div>

              <div class="management-potential-report-page__footer" data-html2canvas-ignore>
                <a-button class="secondary-button" @click="scrollTop">
                  Наверх
                </a-button>
              </div>
            </div>
          </ReferenceSection>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  DownloadOutlined,
  InfoCircleFilled,
  LeftOutlined,
} from '@ant-design/icons-vue'
import html2pdf from 'html2pdf.js'

import Preloader from '@/components/Technical/Preloader.vue'
import ReferenceSection from '@/views/Report/NewProfileReport/ReferenceSection.vue'
import ReportNextStepPromotionCard from '@/components/Cards/ReportNextStepPromotionCard.vue'

import ManagementPotentialInstruction from '@/views/Report/ManagementPotentialReport/ManagementPotentialInstruction.vue'
import ReportDetailsHeader from '@/feature/analytics/report-parameters/ReportDetailsHeader.vue'
import ManagementPotentialLevelsChart from '@/views/Report/ManagementPotentialReport/ManagementPotentialLevelsChart.vue'
import ManagementFlagsBlock from '@/views/Report/ManagementPotentialReport/ManagementFlagsBlock.vue'

import AgeEiProfileComparisonBlock from '@/views/Report/ManagementPotentialReport/AgeEiProfileComparisonBlock.vue'
import DemographicRoleDistributionCard from '@/views/Report/ManagementPotentialReport/DemographicRoleDistributionCard.vue'
import AgeEiLevelGroupedChart from '@/views/Report/ManagementPotentialReport/AgeEiLevelGroupedChart.vue'
import GenderEiProfileComparisonBlock from '@/views/Report/ManagementPotentialReport/GenderEiProfileComparisonBlock.vue'
import GenderEiLevelCompareChart from '@/views/Report/ManagementPotentialReport/GenderEiLevelCompareChart.vue'
import BranchDonutCard from '@/views/Report/ManagementPotentialReport/BranchDonutCard.vue'
import EiScaleRowsCard from '@/views/Report/ManagementPotentialReport/EiScalesRowCard.vue'
import EiLevelEmployeesChart from '@/views/Report/ManagementPotentialReport/EiLevelEmployeeChart.vue'

import EiGrowthProfileBlock from '@/components/Analytics/BenchmarkDetails.vue'
import EiGeneralScale from '@/components/Analytics/GeneralBenchmarkDetail.vue'

import RoleDistributionCard from '@/views/Report/CompareReport/RoleDistributionCard.vue'
import DomainDisbalanceCard from '@/views/Report/CompareReport/DomainDisbalanceCard.vue'
import EiStrengthGrowthProfileBlock from '@/views/Report/ManagementPotentialReport/EiStrengthGrowthProfileBlock.vue'
import {useStore} from "vuex";

export default {
  name: 'ManagementPotentialReport',

  components: {
    EiStrengthGrowthProfileBlock,
    ManagementPotentialLevelsChart,
    ManagementFlagsBlock,
    EiGeneralScale,
    EiLevelEmployeesChart,
    DomainDisbalanceCard,
    RoleDistributionCard,
    EiScaleRowsCard,
    BranchDonutCard,
    EiGrowthProfileBlock,
    GenderEiLevelCompareChart,
    GenderEiProfileComparisonBlock,
    AgeEiLevelGroupedChart,
    DemographicRoleDistributionCard,
    AgeEiProfileComparisonBlock,
    ReportDetailsHeader,
    ManagementPotentialInstruction,
    LeftOutlined,
    DownloadOutlined,
    InfoCircleFilled,
    Preloader,
    ReferenceSection,
    ReportNextStepPromotionCard,
  },

  setup() {
    const router = useRouter()
    const store = useStore()

    const isPreloader = ref(false)
    const isPrinting = ref(false)

    const openedSections = reactive({
      managementPotential: true,
      emotionalIntelligence: true,
      strategistsAndDrivers: true,
      demographicsComparison: true,
    })

    const RED_COLOR = '#F55565'
    const GREY_COLOR = '#D5DBE2'
    const GREEN_COLOR = '#21AC0C'
    const EI_GREEN_COLOR = '#99DA6E'
    const BRANCH_HIGHLIGHT_GAP = 2

    const GENDER_MALE_COLOR = '#7D9EE9'
    const GENDER_FEMALE_COLOR = '#F29EB6'

    const ROLE_COLORS = {
      drivers: '#A29BFE',
      strategists: '#71E5E0',
      balanced: '#D5DBE2',
    }

    const BRANCH_COLORS = {
      branch_1: '#6C5CE7',
      branch_2: '#B6AFFF',
      branch_3: '#00B5AD',
      branch_4: '#71E5E0',
    }

    const AGE_GROUPS = [
      { key: '18-25', label: '18–25', fullLabel: '18–25 лет' },
      { key: '26-35', label: '26–35', fullLabel: '26–35 лет' },
      { key: '36-55', label: '36–55', fullLabel: '36–55 лет' },
      { key: '56+', label: '56+', fullLabel: '56+ лет' },
    ]

    const DETAILS_HELP_TEXTS = {
      section_1: 'Раздел направлен на эмоциональное оценивание лиц.',
      section_5: 'Раздел направлен на измерение способности определять эмоции в окружающем пространстве, среде и произведениях искусства.',
      section_9a: 'Раздел направлен на оценку способности распознавать эмоции в динамике и с учетом дополнительных факторов.',
      section_2: 'Раздел направлен на измерение способности определять эмоции, эффективные для содействия мышлению в конкретной деятельности.',
      section_6: 'Раздел направлен на измерение способности описывать свои эмоциональные состояния и почувствовать эмоции другого.',
      section_3: 'Раздел направлен на измерение способности понимать особенности перехода эмоций друг в друга.',
      section_7: 'Раздел направлен на диагностику способности различать эмоции, входящие в комплексные чувства.',
      section_9b: 'Раздел направлен на измерение способности оценивать динамику эмоциональных состояний с учетом внешних факторов.',
      section_4: 'Раздел направлен на изучение способности регулировать собственные эмоциональные состояния.',
      section_8: 'Раздел направлен на измерение способности регулировать эмоциональные состояния других людей.',
    }

    const branchLabels = {
      branch_1: 'Распознавание эмоций',
      branch_2: 'Использование эмоций',
      branch_3: 'Анализ эмоций',
      branch_4: 'Управление эмоциями',
    }

    const source = computed(() => store.state.analytics?.reportData || {})

    const normalizeAgeKey = (key) => {
      return String(key || '')
          .trim()
          .replace(/\s+/g, '')
          .replace(/–/g, '-')
          .replace(/—/g, '-')
          .replace(/лет/gi, '')
    }

    const getAgeShortLabel = (key) => {
      return AGE_GROUPS.find((item) => item.key === key)?.label || key
    }

    const getAgeFullLabel = (key) => {
      return AGE_GROUPS.find((item) => item.key === key)?.fullLabel || key
    }

    const toNumber = (value, fallback = 0) => {
      if (value === null || value === undefined || value === '') {
        return fallback
      }

      const number = Number(value)

      return Number.isFinite(number) ? Math.round(number) : fallback
    }

    const hasDataValue = (value) => {
      return value !== null && value !== undefined && value !== ''
    }

    const getMaxExistingValue = (items, valueGetter) => {
      const values = items
          .map((item) => valueGetter(item))
          .filter((item) => hasDataValue(item.raw))
          .map((item) => item.value)

      if (!values.length) {
        return null
      }

      return Math.max(...values)
    }

    const isMaxExistingValue = (value, maxValue) => {
      return maxValue !== null && maxValue > 0 && value === maxValue
    }

    const makeScaleRow = (key, label, value, helpText = '', forcedColor = null) => {
      const number = toNumber(value)

      return {
        key,
        label,
        value: number,
        helpText,
        color: forcedColor || (hasDataValue(value) ? (number >= 100 ? EI_GREEN_COLOR : RED_COLOR) : GREY_COLOR),
      }
    }

    const getBranchHighlightKeys = (branches, direction) => {
      const branchValues = Object.keys(branchLabels)
          .map((key) => ({
            key,
            raw: branches[key],
            value: toNumber(branches[key]),
          }))
          .filter((item) => hasDataValue(item.raw) && item.value > 0)

      if (!branchValues.length) {
        return new Set()
      }

      const sortedValues = [...branchValues].sort((a, b) => {
        return direction === 'highest'
            ? b.value - a.value
            : a.value - b.value
      })
      const selectedKeys = new Set([sortedValues[0].key])
      const secondValue = sortedValues[1]

      if (
          secondValue &&
          Math.abs(sortedValues[0].value - secondValue.value) <= BRANCH_HIGHLIGHT_GAP
      ) {
        selectedKeys.add(secondValue.key)
      }

      return selectedKeys
    }

    const getRankedScaleRowKeys = (rows, direction, limit, excludedKeys = new Set()) => {
      return new Set(
          rows
              .map((row, index) => ({ row, index }))
              .filter(({ row }) => row.value > 0 && !excludedKeys.has(row.key))
              .sort((a, b) => {
                const valueDiff = direction === 'highest'
                    ? b.row.value - a.row.value
                    : a.row.value - b.row.value

                return valueDiff || a.index - b.index
              })
              .slice(0, limit)
              .map(({ row }) => row.key)
      )
    }

    const applyRankedScaleColors = (rows, highestLimit, lowestLimit) => {
      const highestKeys = getRankedScaleRowKeys(rows, 'highest', highestLimit)
      const lowestKeys = getRankedScaleRowKeys(rows, 'lowest', lowestLimit, highestKeys)

      return rows.map((row) => ({
        ...row,
        color: highestKeys.has(row.key)
            ? EI_GREEN_COLOR
            : lowestKeys.has(row.key)
                ? RED_COLOR
                : GREY_COLOR,
      }))
    }

    const applyStrengthGrowthProfileColors = (sections) => {
      return sections.map((section) => {
        if (section.key === 'domains' || section.key === 'branches') {
          return {
            ...section,
            rows: applyRankedScaleColors(section.rows, 1, 1),
          }
        }

        if (section.key === 'sections') {
          return {
            ...section,
            rows: applyRankedScaleColors(section.rows, 2, 2),
          }
        }

        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            color: GREY_COLOR,
          })),
        }
      })
    }

    const makeCompareScaleRow = (key, label, maleValue, femaleValue, helpText = '') => {
      return {
        key,
        label,
        value: toNumber(femaleValue),
        compareValue: toNumber(maleValue),
        helpText,
        color: hasDataValue(femaleValue) ? GENDER_FEMALE_COLOR : GREY_COLOR,
        compareColor: hasDataValue(maleValue) ? GENDER_MALE_COLOR : GREY_COLOR,
      }
    }

    const normalizeDemographicList = (data) => {
      if (Array.isArray(data)) {
        return data
            .map((item) => ({
              key: item?.key || item?.gender || item?.age_group || item?.ageGroup || item?.label,
              data: item,
            }))
            .filter((item) => item.key)
      }

      if (data && typeof data === 'object') {
        return Object.entries(data).map(([key, value]) => ({
          key,
          data: value,
        }))
      }

      return []
    }

    const genderItems = computed(() => normalizeDemographicList(source.value?.demographic?.by_gender))
    const ageItems = computed(() => normalizeDemographicList(source.value?.demographic?.by_age))

    const ageItemsMap = computed(() => {
      return ageItems.value.reduce((result, entry) => {
        result[normalizeAgeKey(entry.key)] = entry.data || {}

        return result
      }, {})
    })

    const visibleAgeItems = computed(() => {
      const defaultItems = AGE_GROUPS.map((group) => ({
        key: group.key,
        label: group.label,
        fullLabel: group.fullLabel,
        data: ageItemsMap.value[group.key] || {},
      }))

      const defaultKeys = AGE_GROUPS.map((group) => group.key)

      const extraItems = ageItems.value
          .map((entry) => {
            const normalizedKey = normalizeAgeKey(entry.key)

            return {
              key: normalizedKey,
              label: getAgeShortLabel(normalizedKey),
              fullLabel: getAgeFullLabel(normalizedKey),
              data: entry.data || {},
            }
          })
          .filter((entry) => entry.key && !defaultKeys.includes(entry.key))

      return [
        ...defaultItems,
        ...extraItems,
      ]
    })

    const maleGenderData = computed(() => {
      const item = genderItems.value.find((entry) => {
        const key = String(entry.key).toLowerCase()

        return ['male', 'm', 'man', 'men', 'м', 'муж', 'мужчина', 'мужчины', '1'].includes(key)
      })

      return item?.data || null
    })

    const femaleGenderData = computed(() => {
      const item = genderItems.value.find((entry) => {
        const key = String(entry.key).toLowerCase()

        return ['female', 'f', 'woman', 'women', 'ж', 'жен', 'женщина', 'женщины', '2'].includes(key)
      })

      return item?.data || null
    })

    const managementPotentialLevels = computed(() => {
      const rawItems = source.value?.management?.management_potential_score || []
      const hasSourceItems = Array.isArray(rawItems) && rawItems.length > 0

      const mapByType = (Array.isArray(rawItems) ? rawItems : []).reduce((result, item) => {
        result[item.type] = item

        return result
      }, {})

      return [
        {
          sourceType: 'LEVEL_POTENTIAL_LOW',
          key: 'low',
          label: 'Низкий',
          color: RED_COLOR,
        },
        {
          sourceType: 'LEVEL_POTENTIAL_MODERATE_RISKS',
          key: 'risk',
          label: 'Умеренный',
          color: '#FF9F43',
        },
        {
          sourceType: 'LEVEL_POTENTIAL_MODERATE',
          key: 'moderate',
          label: 'Средний',
          color: '#DDD71D',
        },
        {
          sourceType: 'LEVEL_POTENTIAL_HIGHEST',
          key: 'high',
          label: 'Высокий',
          color: EI_GREEN_COLOR,
        },
        {
          sourceType: 'LEVEL_POTENTIAL_OVER_HIGH',
          key: 'highest',
          label: 'Максимальный',
          color: GREEN_COLOR,
        },
      ].map((item) => ({
        key: item.key,
        label: mapByType[item.sourceType]?.label || item.label,
        value: toNumber(mapByType[item.sourceType]?.count),
        color: hasSourceItems ? item.color : GREY_COLOR,
      }))
    })

    const managementFlags = computed(() => {
      const flags = source.value?.management?.detailed_flags || {}
      const balance = flags.group_balance || {}
      const risks = flags.risks || {}

      const balancedTotal = toNumber(balance.highest_balanced) + toNumber(balance.high_balanced)

      const risksTotal = toNumber(risks.burnout_risk) +
          toNumber(risks.manipulative_risk) +
          toNumber(risks.led_risk) +
          toNumber(risks.influence_risk) +
          toNumber(risks.impulsivity_risk)

      const moderateTotal = toNumber(flags.moderate_profiles)

      return {
        groups: [
          {
            key: 'balanced',
            title: 'Сотрудники с балансом ЭИ',
            total: balancedTotal,
            color: GREEN_COLOR,
            items: [
              {
                key: 'highest_balanced',
                label: 'В высоких границах, < 120',
                value: toNumber(balance.highest_balanced),
                change: null,
              },
              {
                key: 'high_balanced',
                label: 'В средних границах, 100–120',
                value: toNumber(balance.high_balanced),
                change: null,
              },
            ],
          },
          {
            key: 'risks',
            title: 'Сотрудники с рисками',
            total: risksTotal,
            color: RED_COLOR,
            items: [
              {
                key: 'burnout_risk',
                label: 'Выгорание',
                value: toNumber(risks.burnout_risk),
                change: null,
              },
              {
                key: 'manipulative_risk',
                label: 'Манипулятивность',
                value: toNumber(risks.manipulative_risk),
                change: null,
              },
              {
                key: 'influence_risk',
                label: 'Не способен влиять на эмоции других',
                value: toNumber(risks.led_risk ?? risks.influence_risk),
                change: null,
              },
              {
                key: 'impulsivity_risk',
                label: 'Риск импульсивности',
                value: toNumber(risks.impulsivity_risk),
                change: null,
              },
            ],
          },
          {
            key: 'moderate',
            title: 'Умеренный профиль',
            total: moderateTotal,
            color: GREY_COLOR,
            items: [],
          },
        ],
        segments: [
          { key: 'risks', value: risksTotal, color: RED_COLOR },
          { key: 'moderate', value: moderateTotal, color: GREY_COLOR },
          { key: 'balanced', value: balancedTotal, color: GREEN_COLOR },
        ].filter((item) => item.value > 0),
      }
    })

    const eiGeneralScale = computed(() => ({
      compareValue: 0,
      value: toNumber(source.value?.ei?.average_ei_scores?.total),
    }))

    const eiLevelBars = computed(() => {
      const levels = source.value?.ei?.count_employee_level_ei || {}
      const hasLevels = Object.keys(levels).length > 0

      const items = [
        { key: 'low', label: 'Низкий', value: toNumber(levels.low), color: RED_COLOR },
        { key: 'medium', label: 'Средний', value: toNumber(levels.medium), color: '#FF9F43' },
        { key: 'high', label: 'Высокий', value: toNumber(levels.high), color: EI_GREEN_COLOR },
        { key: 'highest', label: 'Высший', value: toNumber(levels.highest), color: GREEN_COLOR },
      ]

      return hasLevels ? items : items.map((item) => ({ ...item, color: GREY_COLOR }))
    })

    const roleDistribution = computed(() => {
      const counts = source.value?.ei?.strategies_and_drivers?.count_employee || {}

      return [
        { key: 'drivers', label: 'Драйверы', value: toNumber(counts.drivers), color: ROLE_COLORS.drivers },
        { key: 'strategists', label: 'Стратеги', value: toNumber(counts.strategists), color: ROLE_COLORS.strategists },
        { key: 'balanced', label: 'Сбалансированные', value: toNumber(counts.balanced), color: ROLE_COLORS.balanced },
      ]
    })

    const domainDisbalance = computed(() => {
      const imbalance = source.value?.ei?.strategies_and_drivers?.level_imbalance || {}

      return [
        {
          key: 'drivers',
          label: 'Драйверы',
          average: toNumber(imbalance.drivers?.avg),
          min: toNumber(imbalance.drivers?.min),
          max: toNumber(imbalance.drivers?.max),
        },
        {
          key: 'strategists',
          label: 'Стратеги',
          average: toNumber(imbalance.strategists?.avg),
          min: toNumber(imbalance.strategists?.min),
          max: toNumber(imbalance.strategists?.max),
        },
      ]
    })

    const strongSides = computed(() => {
      const branches = source.value?.ei?.strengths?.branches || {}
      const highlightedBranches = getBranchHighlightKeys(branches, 'highest')

      return [
        {
          key: 'branches',
          title: null,
          rows: Object.keys(branchLabels).map((key) => {
            return makeScaleRow(
                key,
                branchLabels[key],
                branches[key],
                '',
                highlightedBranches.has(key) ? EI_GREEN_COLOR : GREY_COLOR
            )
          }),
        },
      ]
    })

    const leadingBranches = computed(() => {
      const leading = source.value?.ei?.strengths?.leading || {}

      return Object.keys(branchLabels).map((key) => ({
        key,
        label: branchLabels[key],
        value: toNumber(leading[`${key}_count`]),
        color: BRANCH_COLORS[key],
      }))
    })

    const growthZones = computed(() => {
      const branches = source.value?.ei?.growth_zones?.branches || {}
      const highlightedBranches = getBranchHighlightKeys(branches, 'lowest')

      return [
        {
          key: 'branches',
          title: null,
          rows: Object.keys(branchLabels).map((key) => {
            return makeScaleRow(
                key,
                branchLabels[key],
                branches[key],
                '',
                highlightedBranches.has(key) ? RED_COLOR : GREY_COLOR
            )
          }),
        },
      ]
    })

    const developmentPriorities = computed(() => {
      const priorities = source.value?.ei?.growth_zones?.development_priorities || {}

      return Object.keys(branchLabels).map((key) => ({
        key,
        label: branchLabels[key],
        value: toNumber(priorities[`${key}_count`]),
        color: BRANCH_COLORS[key],
      }))
    })

    const eiGrowthProfile = computed(() => {
      const scores = source.value?.ei?.average_ei_scores || {}

      return applyStrengthGrowthProfileColors([
        {
          key: 'general',
          title: '',
          rows: [
            makeScaleRow('total', 'Общий ЭИ', scores.total, 'Общий показатель эмоционального интеллекта.'),
          ],
        },
        {
          key: 'domains',
          title: 'Домены',
          rows: [
            makeScaleRow('domain_1', 'Опытный домен', scores.domain_1, 'Способность воспринимать эмоциональную информацию и реагировать на нее.'),
            makeScaleRow('domain_2', 'Стратегический домен', scores.domain_2, 'Способность понимать эмоциональную информацию и использовать ее стратегически.'),
          ],
        },
        {
          key: 'branches',
          title: 'Ветви',
          rows: [
            makeScaleRow('branch_1', 'Распознавание эмоций', scores.branch_1, 'Способность распознавать эмоции по лицам, ситуациям, среде и контексту.'),
            makeScaleRow('branch_2', 'Использование эмоций', scores.branch_2, 'Способность использовать эмоции как ресурс мышления и деятельности.'),
            makeScaleRow('branch_3', 'Анализ эмоций', scores.branch_3, 'Способность понимать причины, переходы и динамику эмоциональных состояний.'),
            makeScaleRow('branch_4', 'Управление эмоциями', scores.branch_4, 'Способность регулировать свои эмоции и влиять на эмоции других.'),
          ],
        },
      ])
    })

    const eiDetailsProfile = computed(() => {
      const details = source.value?.ei?.ei_abilities_detail || {}

      const sectionRows = [
        ['section_1', 'Распознавание эмоций в лицах'],
        ['section_5', 'Распознавание эмоций в среде'],
        ['section_9a', 'Распознавание эмоций в контексте ситуаций'],
        ['section_2', 'Применение эмоций в действиях'],
        ['section_6', 'Эмпатия'],
        ['section_3', 'Анализ изменений эмоций'],
        ['section_7', 'Анализ соединения эмоций в чувства'],
        ['section_9b', 'Анализ динамики эмоций в контексте ситуаций'],
        ['section_4', 'Регуляция своих эмоций'],
        ['section_8', 'Влияние на эмоции других'],
      ]

      return applyStrengthGrowthProfileColors([
        {
          key: 'sections',
          title: 'Разделы',
          rows: sectionRows.map(([key, label]) => makeScaleRow(
              key,
              label,
              details[key] ?? details[`company_${key}`],
              DETAILS_HELP_TEXTS[key],
          )),
        },
      ])
    })

    const genderLevelBars = computed(() => {
      const maleLevels = maleGenderData.value?.count_employee_level_ei || {}
      const femaleLevels = femaleGenderData.value?.count_employee_level_ei || {}
      const hasMaleLevels = Object.keys(maleLevels).length > 0
      const hasFemaleLevels = Object.keys(femaleLevels).length > 0

      return [
        {
          key: 'low',
          label: 'Низкий',
          firstValue: toNumber(maleLevels.low),
          secondValue: toNumber(femaleLevels.low),
          firstColor: hasMaleLevels ? GENDER_MALE_COLOR : GREY_COLOR,
          secondColor: hasFemaleLevels ? GENDER_FEMALE_COLOR : GREY_COLOR,
        },
        {
          key: 'medium',
          label: 'Средний',
          firstValue: toNumber(maleLevels.medium),
          secondValue: toNumber(femaleLevels.medium),
          firstColor: hasMaleLevels ? GENDER_MALE_COLOR : GREY_COLOR,
          secondColor: hasFemaleLevels ? GENDER_FEMALE_COLOR : GREY_COLOR,
        },
        {
          key: 'high',
          label: 'Высокий',
          firstValue: toNumber(maleLevels.high),
          secondValue: toNumber(femaleLevels.high),
          firstColor: hasMaleLevels ? GENDER_MALE_COLOR : GREY_COLOR,
          secondColor: hasFemaleLevels ? GENDER_FEMALE_COLOR : GREY_COLOR,
        },
        {
          key: 'highest',
          label: 'Высший',
          firstValue: toNumber(maleLevels.highest),
          secondValue: toNumber(femaleLevels.highest),
          firstColor: hasMaleLevels ? GENDER_MALE_COLOR : GREY_COLOR,
          secondColor: hasFemaleLevels ? GENDER_FEMALE_COLOR : GREY_COLOR,
        },
      ]
    })

    const genderAverageProfile = computed(() => {
      const male = maleGenderData.value?.average_ei_scores || {}
      const female = femaleGenderData.value?.average_ei_scores || {}

      return [
        {
          key: 'general',
          title: '',
          rows: [
            makeCompareScaleRow('total', 'Общий ЭИ', male.total, female.total, 'Общий показатель эмоционального интеллекта.'),
          ],
        },
        {
          key: 'domains',
          title: 'Домены',
          rows: [
            makeCompareScaleRow('domain_1', 'Опытный домен', male.domain_1, female.domain_1, 'Способность воспринимать эмоциональную информацию и реагировать на нее.'),
            makeCompareScaleRow('domain_2', 'Стратегический домен', male.domain_2, female.domain_2, 'Способность понимать эмоциональную информацию и использовать ее стратегически.'),
          ],
        },
        {
          key: 'branches',
          title: 'Ветви',
          rows: [
            makeCompareScaleRow('branch_1', 'Распознавание эмоций', male.branch_1, female.branch_1, 'Способность распознавать эмоции по лицам, ситуациям, среде и контексту.'),
            makeCompareScaleRow('branch_2', 'Использование эмоций', male.branch_2, female.branch_2, 'Способность использовать эмоции как ресурс мышления и деятельности.'),
            makeCompareScaleRow('branch_3', 'Анализ эмоций', male.branch_3, female.branch_3, 'Способность понимать причины, переходы и динамику эмоциональных состояний.'),
            makeCompareScaleRow('branch_4', 'Управление эмоциями', male.branch_4, female.branch_4, 'Способность регулировать свои эмоции и влиять на эмоции других.'),
          ],
        },
      ]
    })

    const genderDetailsProfile = computed(() => {
      const male = maleGenderData.value?.ei_abilities_detail || {}
      const female = femaleGenderData.value?.ei_abilities_detail || {}

      const rows = [
        ['section_1', 'Распознавание эмоций в лицах'],
        ['section_5', 'Распознавание эмоций в среде'],
        ['section_9a', 'Распознавание эмоций в контексте ситуаций'],
        ['section_2', 'Применение эмоций в действиях'],
        ['section_6', 'Эмпатия'],
        ['section_3', 'Анализ изменений эмоций'],
        ['section_7', 'Анализ соединения эмоций в чувства'],
        ['section_9b', 'Анализ динамики эмоций в контексте ситуаций'],
        ['section_4', 'Регуляция своих эмоций'],
        ['section_8', 'Влияние на эмоции других'],
      ].map(([key, label]) => makeCompareScaleRow(
          key,
          label,
          male[key] ?? male[`company_${key}`],
          female[key] ?? female[`company_${key}`],
          DETAILS_HELP_TEXTS[key],
      ))

      return [
        {
          key: 'sections',
          title: 'Разделы',
          rows,
        },
      ]
    })

    const genderRoleLegend = computed(() => [
      { key: 'drivers', label: 'Драйверы', color: ROLE_COLORS.drivers },
      { key: 'strategists', label: 'Стратеги', color: ROLE_COLORS.strategists },
      { key: 'balanced', label: 'Сбалансированные', color: ROLE_COLORS.balanced },
    ])

    const genderRoleDistribution = computed(() => {
      const male = maleGenderData.value?.strategies_and_drivers?.count_employee || {}
      const female = femaleGenderData.value?.strategies_and_drivers?.count_employee || {}

      return [
        {
          key: 'male',
          label: 'Мужчины',
          items: [
            { key: 'drivers', value: toNumber(male.drivers), color: ROLE_COLORS.drivers },
            { key: 'strategists', value: toNumber(male.strategists), color: ROLE_COLORS.strategists },
            { key: 'balanced', value: toNumber(male.balanced), color: ROLE_COLORS.balanced },
          ],
        },
        {
          key: 'female',
          label: 'Женщины',
          items: [
            { key: 'drivers', value: toNumber(female.drivers), color: ROLE_COLORS.drivers },
            { key: 'strategists', value: toNumber(female.strategists), color: ROLE_COLORS.strategists },
            { key: 'balanced', value: toNumber(female.balanced), color: ROLE_COLORS.balanced },
          ],
        },
      ]
    })

    const genderDomainDisbalance = computed(() => {
      const male = maleGenderData.value?.strategies_and_drivers || {}
      const female = femaleGenderData.value?.strategies_and_drivers || {}

      return [
        {
          key: 'male',
          label: 'Мужчины',
          source: male,
        },
        {
          key: 'female',
          label: 'Женщины',
          source: female,
        },
      ].map((group) => {
        const driversAverage = toNumber(group.source?.level_imbalance?.drivers?.avg)
        const strategistsAverage = toNumber(group.source?.level_imbalance?.strategists?.avg)

        return {
          key: group.key,
          label: group.label,
          rows: [
            {
              key: `${group.key}-drivers`,
              domain: 'Драйверы',
              average: driversAverage,
              min: toNumber(group.source?.level_imbalance?.drivers?.min),
              max: toNumber(group.source?.level_imbalance?.drivers?.max),
              averageStatus: driversAverage <= 10 ? 'green' : 'red',
            },
            {
              key: `${group.key}-strategists`,
              domain: 'Стратеги',
              average: strategistsAverage,
              min: toNumber(group.source?.level_imbalance?.strategists?.min),
              max: toNumber(group.source?.level_imbalance?.strategists?.max),
              averageStatus: strategistsAverage <= 10 ? 'green' : 'red',
            },
          ],
        }
      })
    })

    const ageLevelGroups = computed(() => {
      const levelLabels = {
        low: 'Низкий',
        medium: 'Средний',
        high: 'Высокий',
        highest: 'Высший',
      }

      return Object.keys(levelLabels).map((levelKey) => {
        const maxValue = getMaxExistingValue(visibleAgeItems.value, (entry) => {
          const raw = entry.data?.count_employee_level_ei?.[levelKey]

          return {
            raw,
            value: toNumber(raw),
          }
        })

        return {
          key: levelKey,
          label: levelLabels[levelKey],
          items: visibleAgeItems.value.map((entry) => {
            const raw = entry.data?.count_employee_level_ei?.[levelKey]
            const value = toNumber(raw)

            return {
              key: entry.key,
              label: entry.label,
              value,
              color: isMaxExistingValue(value, maxValue) && hasDataValue(raw)
                  ? EI_GREEN_COLOR
                  : GREY_COLOR,
            }
          }),
        }
      })
    })

    const ageRoleLegend = computed(() => [
      { key: 'drivers', label: 'Драйверы', color: ROLE_COLORS.drivers },
      { key: 'strategists', label: 'Стратеги', color: ROLE_COLORS.strategists },
      { key: 'balanced', label: 'Сбалансированные', color: ROLE_COLORS.balanced },
    ])

    const ageRoleDistribution = computed(() => {
      return visibleAgeItems.value.map((entry) => {
        const counts = entry.data?.strategies_and_drivers?.count_employee || {}

        return {
          key: entry.key,
          label: entry.fullLabel,
          items: [
            { key: 'drivers', value: toNumber(counts.drivers), color: ROLE_COLORS.drivers },
            { key: 'strategists', value: toNumber(counts.strategists), color: ROLE_COLORS.strategists },
            { key: 'balanced', value: toNumber(counts.balanced), color: ROLE_COLORS.balanced },
          ],
        }
      })
    })

    const ageDomainDisbalance = computed(() => {
      return visibleAgeItems.value.map((entry) => {
        const imbalance = entry.data?.strategies_and_drivers?.level_imbalance || {}
        const driversAverage = toNumber(imbalance.drivers?.avg)
        const strategistsAverage = toNumber(imbalance.strategists?.avg)

        return {
          key: entry.key,
          label: entry.fullLabel,
          rows: [
            {
              key: `${entry.key}-drivers`,
              domain: 'Драйверы',
              average: driversAverage,
              min: toNumber(imbalance.drivers?.min),
              max: toNumber(imbalance.drivers?.max),
              averageStatus: driversAverage <= 10 ? 'green' : 'red',
            },
            {
              key: `${entry.key}-strategists`,
              domain: 'Стратеги',
              average: strategistsAverage,
              min: toNumber(imbalance.strategists?.min),
              max: toNumber(imbalance.strategists?.max),
              averageStatus: strategistsAverage <= 10 ? 'green' : 'red',
            },
          ],
        }
      })
    })

    const ageAverageProfile = computed(() => {
      const rowsFor = (items) => {
        return items.map(([key, label, helpText]) => {
          const maxValue = getMaxExistingValue(visibleAgeItems.value, (entry) => {
            const raw = entry.data?.average_ei_scores?.[key]

            return {
              raw,
              value: toNumber(raw),
            }
          })

          return {
            key,
            label,
            helpText,
            items: visibleAgeItems.value.map((entry) => {
              const raw = entry.data?.average_ei_scores?.[key]
              const value = toNumber(raw)

              return {
                key: entry.key,
                label: entry.label,
                value,
                color: isMaxExistingValue(value, maxValue) && hasDataValue(raw)
                    ? EI_GREEN_COLOR
                    : GREY_COLOR,
              }
            }),
          }
        })
      }

      return [
        {
          key: 'general',
          title: '',
          rows: rowsFor([
            ['total', 'Общий ЭИ', 'Общий показатель эмоционального интеллекта.'],
          ]),
        },
        {
          key: 'domains',
          title: 'Домены',
          rows: rowsFor([
            ['domain_1', 'Опытный домен', 'Способность воспринимать эмоциональную информацию и реагировать на нее.'],
            ['domain_2', 'Стратегический домен', 'Способность понимать эмоциональную информацию и использовать ее стратегически.'],
          ]),
        },
        {
          key: 'branches',
          title: 'Ветви',
          rows: rowsFor([
            ['branch_1', 'Распознавание эмоций', 'Способность распознавать эмоции по лицам, ситуациям, среде и контексту.'],
            ['branch_2', 'Использование эмоций', 'Способность использовать эмоции как ресурс мышления и деятельности.'],
            ['branch_3', 'Анализ эмоций', 'Способность понимать причины, переходы и динамику эмоциональных состояний.'],
            ['branch_4', 'Управление эмоциями', 'Способность регулировать свои эмоции и влиять на эмоции других.'],
          ]),
        },
      ]
    })

    const ageDetailsProfile = computed(() => {
      const rows = [
        ['section_1', 'Распознавание эмоций в лицах'],
        ['section_5', 'Распознавание эмоций в среде'],
        ['section_9a', 'Распознавание эмоций в контексте ситуаций'],
        ['section_2', 'Применение эмоций в действиях'],
        ['section_6', 'Эмпатия'],
        ['section_3', 'Анализ изменений эмоций'],
        ['section_7', 'Анализ соединения эмоций в чувства'],
        ['section_9b', 'Анализ динамики эмоций в контексте ситуаций'],
        ['section_4', 'Регуляция своих эмоций'],
        ['section_8', 'Влияние на эмоции других'],
      ].map(([key, label]) => {
        const maxValue = getMaxExistingValue(visibleAgeItems.value, (entry) => {
          const details = entry.data?.ei_abilities_detail || {}
          const raw = details[key] ?? details[`company_${key}`]

          return {
            raw,
            value: toNumber(raw),
          }
        })

        return {
          key,
          label,
          helpText: DETAILS_HELP_TEXTS[key],
          items: visibleAgeItems.value.map((entry) => {
            const details = entry.data?.ei_abilities_detail || {}
            const raw = details[key] ?? details[`company_${key}`]
            const value = toNumber(raw)

            return {
              key: entry.key,
              label: entry.label,
              value,
              color: isMaxExistingValue(value, maxValue) && hasDataValue(raw)
                  ? EI_GREEN_COLOR
                  : GREY_COLOR,
            }
          }),
        }
      })

      return [
        {
          key: 'sections',
          title: 'Разделы',
          rows,
        },
      ]
    })

    const toggleSection = (key) => {
      openedSections[key] = !openedSections[key]
    }

    const print = () => {
      isPrinting.value = true

      html2pdf(document.getElementById('print_element'), {
        autoPaging: 'text',
        margin: [12, 8, 15, 8],
        filename: `management_potential_${Date.now()}.pdf`,
        html2canvas: {
          dpi: 192,
          scale: 2,
          windowWidth: 1000,
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          before: ['.pdf-page-break'],
          avoid: ['.pdf-avoid-break'],
        },
      }).then(() => {
        isPrinting.value = false
      })
    }

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back()
      } else {
        router.push(url)
      }
    }

    const scrollTop = () => {
      const container = document.querySelector('.page-container')

      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })

        return
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    return {
      isPreloader,
      isPrinting,
      openedSections,

      managementPotentialLevels,
      managementFlags,

      eiGeneralScale,
      eiLevelBars,
      roleDistribution,
      domainDisbalance,
      strongSides,
      leadingBranches,
      growthZones,
      developmentPriorities,
      eiGrowthProfile,
      eiDetailsProfile,

      genderLevelBars,
      genderAverageProfile,
      genderDetailsProfile,
      genderRoleLegend,
      genderRoleDistribution,
      genderDomainDisbalance,

      ageLevelGroups,
      ageRoleLegend,
      ageRoleDistribution,
      ageDomainDisbalance,
      ageAverageProfile,
      ageDetailsProfile,

      toggleSection,
      print,
      goBack,
      scrollTop,
    }
  },
}
</script>

<style lang="scss" scoped>
.management-potential-report-page {
  width: 100%;
  background: #ffffff;
}

.management-potential-report-page__preloader {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.management-potential-report-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 20px;
  margin-bottom: 32px;
}

.management-potential-report-page__title {
  margin: 0;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
}

.management-potential-report-page__section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.management-potential-report-page__chart-card {
  width: 100%;
  border-radius: 8px;
}

.management-potential-report-page__chart-card :deep(.ant-card-body) {
  padding: 18px 18px 16px;
}

.management-potential-report-page__chart-card--small,
.management-potential-report-page__chart-card--gender {
  width: 100%;
  max-width: none;
}

.management-potential-report-page__chart-card--age {
  width: 100%;
  max-width: none;
  margin-bottom: 16px;
}

.management-potential-report-page__subsection {
  width: 100%;
}

.management-potential-report-page__subsection-title {
  margin: 0 0 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-potential-report-page__ei-chart-grid,
.management-potential-report-page__gender-chart-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.management-potential-report-page__comparison-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
  margin-bottom: 4px;
}

.management-potential-report-page__notice {
  min-height: 34px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px;
  border-radius: 4px;
  background: var(--geek-blue-1);
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-potential-report-page__notice-icon {
  flex: 0 0 auto;
  color: var(--geek-blue);
  font-size: 14px;
}

.management-potential-report-page__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #ffffff;
  border-radius: 25%;
  cursor: pointer;
}

.management-potential-report-page__half-block {
  width: 50%;
  min-width: 0;
}

@media (max-width: $tablet-breakpoint) {
  .management-potential-report-page__ei-chart-grid,
  .management-potential-report-page__gender-chart-grid,
  .management-potential-report-page__comparison-grid {
    grid-template-columns: 1fr;
  }

  .management-potential-report-page__half-block {
    width: 100%;
  }
}

@media (max-width: $mobile-breakpoint) {
  .management-potential-report-page__print {
    width: 100%;
  }

  .management-potential-report-page__header {
    align-items: flex-start;
    flex-direction: column;
    row-gap: 16px;
  }

  .management-potential-report-page__notice {
    align-items: flex-start;
  }

  .management-potential-report-page__footer {
    justify-content: stretch;
  }

  .management-potential-report-page__footer .secondary-button {
    width: 100%;
  }
}

.instruction-wrapper {
  margin-bottom: 20px;
}

.age-ei-profile-comparison-wrapper {
  margin: 16px 0;
}

.management-potential-report-page__roles {
  margin-top: 16px;
}
</style>
